import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, Send, Sparkles, Sun, Video } from 'lucide-react';
import * as simApi from '@/api/simulador.api';
import type { AvisoSimulado, ChatSimulado } from '@/api/simulador.api';
import { ApiError } from '@/api/client';
import { PageHeader } from '@/components/common/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { EscribirComoElDueno } from '@/components/simulador/EscribirComoElDueno';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import {
  TELEFONO_POR_DEFECTO,
  esDeNoche,
  esTelefonoDeLaDemo,
  horaDelNegocio,
  relojDeLaDemo,
  siguienteAdelanto,
} from '@/lib/simulador';
import { LUGAR_DE_LA_HORA } from '@/lib/zona';

/**
 * SIMULADOR (tanda 3, sección 5 de la auditoría): la demo sin terminal.
 *
 * A la izquierda se escribe como un cliente desde un teléfono ficticio
 * (809-555-xxxx) y se ve su chat, con la temperatura que le puso el sistema. A la
 * derecha, el «Teléfono del dueño»: lo que le llega por el canal «Avisos», que se
 * refresca solo. Arriba, el reloj de la demo («sin respuesta» necesita horas que
 * en una demo no pasan), el resumen de las 7 ya, y las conversaciones de ejemplo.
 *
 * Solo existe con SIMULADOR_PERMITIDO=1: sin la bandera la API no registra estas
 * rutas y el menú no ofrece la pantalla. Nada sale por WhatsApp: los dos canales
 * tienen candado.
 */
export default function Simulador() {
  const qc = useQueryClient();
  const toast = useToast();
  const [telefono, setTelefono] = useState(TELEFONO_POR_DEFECTO);
  const [texto, setTexto] = useState('');
  const [adelantoH, setAdelantoH] = useState(0);
  const valido = esTelefonoDeLaDemo(telefono);
  const fallo = (err: unknown, porDefecto: string) => toast.error(err instanceof ApiError ? err.message : porDefecto);
  const refrescarAvisos = () => void qc.invalidateQueries({ queryKey: ['simulador', 'avisos'] });

  const chat = useQuery({
    queryKey: ['simulador', 'chat', telefono],
    queryFn: () => simApi.chatDe(telefono),
    enabled: valido,
    refetchInterval: 2_000,
  });
  const avisos = useQuery({ queryKey: ['simulador', 'avisos'], queryFn: simApi.avisos, refetchInterval: 3_000 });

  const enviar = useMutation({
    mutationFn: () => simApi.enviarComoCliente(telefono, texto.trim()),
    onSuccess: () => {
      setTexto('');
      void qc.invalidateQueries({ queryKey: ['simulador', 'chat', telefono] });
    },
    onError: (err) => fallo(err, 'No se pudo mandar el mensaje'),
  });
  const reloj = useMutation({
    mutationFn: (h: number) => simApi.revisarSinRespuesta(relojDeLaDemo(h).toISOString()),
    onSuccess: (r, h) => {
      setAdelantoH(h);
      toast.success(r.avisados ? `Avisos de «sin respuesta»: ${r.avisados}` : 'Ningún chat sin respuesta para avisar');
      refrescarAvisos();
    },
    onError: (err) => fallo(err, 'No se pudo adelantar el reloj'),
  });
  const resumen = useMutation({
    mutationFn: () => simApi.mandarResumen(relojDeLaDemo(adelantoH).toISOString()),
    onSuccess: () => {
      toast.success('Resumen enviado al teléfono de Enrique');
      refrescarAvisos();
    },
    onError: (err) => fallo(err, 'No se pudo mandar el resumen'),
  });
  // Ola C: los recordatorios del Zoom de este cliente, uno por clic, sin esperar días.
  const recordatorioZoom = useMutation({
    mutationFn: () => simApi.proximoRecordatorioZoom(telefono),
    onSuccess: (r) => {
      toast.success(
        !r.momento
          ? 'Este cliente no tiene recordatorios del Zoom pendientes'
          : r.enviados
            ? `Recordatorios del Zoom enviados: ${r.enviados}. Míralos en el chat y en el teléfono de Enrique.`
            : 'A esa hora no hubo nada que avisar (el cliente ya contestó o no se le pidió confirmar). Toca otra vez para el siguiente.',
      );
      refrescarAvisos();
      void qc.invalidateQueries({ queryKey: ['simulador', 'chat', telefono] });
    },
    onError: (err) => fallo(err, 'No se pudieron mandar los recordatorios del Zoom'),
  });
  const demo = useMutation({
    mutationFn: simApi.cargarDemo,
    onSuccess: (r) =>
      toast.success(
        r.iniciada
          ? `Cargando ${r.conversaciones} conversaciones de ejemplo: aparecen en Chats en un minuto`
          : 'Ya se están cargando; espera un momento',
      ),
    onError: (err) => fallo(err, 'No se pudieron cargar las conversaciones de ejemplo'),
  });

  const ahoraDemo = relojDeLaDemo(adelantoH);
  return (
    <div className="space-y-4">
      <PageHeader
        title="Simulador"
        description="Escribe como un cliente (teléfonos 809-555, ficticios) y mira lo que le llega a Enrique. Nada sale por WhatsApp."
        action={
          <Button variant="outline" size="sm" onClick={() => demo.mutate()} loading={demo.isPending}>
            <Sparkles className="h-4 w-4" /> Cargar conversaciones de ejemplo
          </Button>
        }
      />

      <Card>
        <CardBody className="flex flex-wrap items-center gap-3 p-3 text-sm">
          <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
            <Clock className="h-4 w-4" />
            Reloj de la demo: {adelantoH ? `+${adelantoH} h · ` : ''}
            {horaDelNegocio(ahoraDemo)} de {LUGAR_DE_LA_HORA}
            {esDeNoche(ahoraDemo) && ' (de noche no sale «sin respuesta»)'}
          </span>
          <Button size="sm" variant="outline" onClick={() => reloj.mutate(siguienteAdelanto(adelantoH))} loading={reloj.isPending}>
            Adelantar el reloj 3 h
          </Button>
          <Button size="sm" variant="outline" onClick={() => resumen.mutate()} loading={resumen.isPending}>
            <Sun className="h-4 w-4" /> Mandar el resumen ahora
          </Button>
          <Button size="sm" variant="outline" onClick={() => recordatorioZoom.mutate()} loading={recordatorioZoom.isPending} disabled={!valido}>
            <Video className="h-4 w-4" /> Próximo recordatorio del Zoom
          </Button>
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardBody className="space-y-3 p-4">
            <Input
              label="Teléfono del cliente"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              error={valido ? undefined : 'Usa un teléfono ficticio 809-555-xxxx'}
            />
            <ChatDelClienteView chat={chat.data} />
            <form
              className="flex items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (valido && texto.trim()) enviar.mutate();
              }}
            >
              <Textarea
                aria-label="Mensaje del cliente"
                rows={2}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Hola, vi el anuncio de la masterclass"
                className="text-sm"
              />
              <Button type="submit" disabled={!valido || !texto.trim()} loading={enviar.isPending} aria-label="Enviar como cliente">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </CardBody>
        </Card>
        <TelefonoDelDuenoView avisos={avisos.data ?? []}>
          <EscribirComoElDueno />
        </TelefonoDelDuenoView>
      </div>
    </div>
  );
}

const TEMPERATURA: Record<string, string> = {
  CALIENTE: 'Caliente',
  TIBIO: 'Tibio',
  FRIO: 'Frío',
  LARGO_PLAZO: 'Largo plazo',
  CERRADO: 'Cerrado',
  NO_CERRADO: 'No cerrado',
};

/** El chat del cliente, con su temperatura y el enlace a la conversación en Chats. Sin estado. */
export function ChatDelClienteView({ chat }: { chat: ChatSimulado | undefined }) {
  const mensajes = chat?.mensajes ?? [];
  const c = chat?.conversacion ?? null;
  return (
    <div className="space-y-2">
      {c && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {c.status === 'WAITING_HUMAN' && 'Esperando a una persona · '}
          {c.leadMotivo ?? (c.leadStatus ? TEMPERATURA[c.leadStatus] ?? c.leadStatus : 'Sin clasificar todavía')}
          {' · '}
          <Link to={`/conversations/${c.id}`} className="text-brand-600 hover:underline">
            Abrir en Chats
          </Link>
        </p>
      )}
      {/* flex-col-reverse: lo más nuevo abajo y el scroll arranca abajo. */}
      <div className="flex h-80 flex-col-reverse gap-2 overflow-y-auto rounded-md bg-slate-50 p-3 dark:bg-slate-800/50">
        {mensajes.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">Todavía no hay mensajes con este teléfono.</p>
        ) : (
          [...mensajes].reverse().map((m, i) => (
            <div
              key={`${m.fecha}-${i}`}
              className={cn(
                'max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm',
                m.de === 'cliente'
                  ? 'self-end bg-brand-600 text-white'
                  : 'self-start bg-white text-slate-800 shadow-sm dark:bg-slate-900 dark:text-slate-100',
              )}
            >
              {m.de === 'persona' && <span className="mb-0.5 block text-[11px] font-semibold text-emerald-600">Enrique</span>}
              {m.texto}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/** Lo que le llegó al dueño por «Avisos», como un chat de WhatsApp. Sin estado. */
export function TelefonoDelDuenoView({ avisos, children }: { avisos: AvisoSimulado[]; children?: React.ReactNode }) {
  return (
    <Card className="overflow-hidden">
      <div className="bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white">Teléfono de Enrique · Avisos</div>
      {/* La API los da del más nuevo al más viejo: con flex-col-reverse el más nuevo queda abajo. */}
      <div className="flex h-[28rem] flex-col-reverse gap-2 overflow-y-auto bg-emerald-50 p-3 dark:bg-slate-800">
        {avisos.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">Todavía no le ha llegado ningún aviso.</p>
        ) : (
          avisos.map((a, i) => (
            <div
              key={`${a.fecha}-${i}`}
              className="max-w-[90%] self-start whitespace-pre-wrap rounded-lg bg-white px-3 py-2 text-sm text-slate-800 shadow-sm dark:bg-slate-900 dark:text-slate-100"
            >
              {a.texto}
              <span className="mt-1 block text-right text-[10px] text-slate-400">{horaDelNegocio(new Date(a.fecha))}</span>
            </div>
          ))
        )}
      </div>
      {children}
    </Card>
  );
}
